import {
  ActionIcon,
  Drawer,
  DrawerProps,
  Group,
  NavLink,
  Stack,
} from "@mantine/core";
import { IconChevronRight, IconX } from "@tabler/icons-react";
import { Link } from "gatsby";
import React, { ReactNode } from "react";

import { Text, Title } from "@components";

interface MobileHeaderDrawerLink {
  label: string;
  icon?: ReactNode;
  href?: string;
  onClick?: () => void;
  rightSection?: ReactNode;
}

interface MobileHeaderDrawerProps
  extends Omit<DrawerProps, "children" | "content"> {
  title: ReactNode;
  content?: ReactNode;
  cta?: {
    label: string;
    href: string;
  };
  links: MobileHeaderDrawerLink[];
  onBack?: () => void;
  children?: ReactNode;
}

export function MobileHeaderDrawer({
  title,
  content,
  cta,
  links,
  onBack,
  children,
  ...drawerProps
}: MobileHeaderDrawerProps) {
  return (
    <Drawer
      position="right"
      size="lg"
      padding="xl"
      offset={12}
      radius="sm"
      overlayProps={{ backgroundOpacity: 0 }}
      closeButtonProps={{
        bg: "transparent",
        icon: <IconX size={24} />,
        size: "lg",
      }}
      styles={{
        header: {
          paddingTop: "var(--mantine-spacing-lg)",
          paddingBottom: "var(--mantine-spacing-lg)",
          paddingLeft: onBack ? "var(--mantine-spacing-md)" : undefined,
          borderBottom:
            "1px solid var(--mantine-color-decorativeBorderLight-5)",
          background:
            "linear-gradient(270deg, var(--mantine-color-layoutDark-5) 0%, var(--mantine-color-layoutDarkSecondary-5) 100%)",
          boxShadow: "inset 0 1px 1px 0 rgb(255 255 255 / 9%)",
        },
        body: {
          paddingTop: "var(--mantine-spacing-xl)",
          background:
            "linear-gradient(270deg, var(--mantine-color-layoutDark-5) 0%, var(--mantine-color-layoutDarkSecondary-5) 100%)",
        },
        content: {
          background:
            "linear-gradient(270deg, var(--mantine-color-layoutDark-5) 0%, var(--mantine-color-layoutDarkSecondary-5) 100%)",
          boxShadow: "0 1px 1px 0 rgb(0 0 0 / 25%)",
        },
        inner: {
          right: 0,
        },
        close: {
          color: "var(--mantine-color-fontLight-5)",
        },
      }}
      title={
        onBack ? (
          <Group>
            <ActionIcon
              variant="subtle"
              onClick={onBack}
              size="lg"
              radius="xl"
              styles={{
                root: {
                  color: "var(--mantine-color-fontLight-5)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                },
              }}
            >
              <IconChevronRight
                size={24}
                style={{ transform: "rotate(180deg)" }}
              />
            </ActionIcon>
            {typeof title === "string" ? (
              <Title order={5} c="fontLight">
                {title}
              </Title>
            ) : (
              title
            )}
          </Group>
        ) : typeof title === "string" ? (
          <Title order={5} c="fontLight">
            {title}
          </Title>
        ) : (
          title
        )
      }
      {...drawerProps}
    >
      <Stack gap={content || cta ? "md" : 0}>
        {content &&
          (typeof content === "string" ? (
            <Text size="xs" c="fontLightSecondary">
              {content}
            </Text>
          ) : (
            content
          ))}

        {cta && (
          <Text
            size="xs"
            component={Link}
            td="underline"
            fw="600"
            to={cta.href}
            c="fontLight"
            mb="sm"
            onClick={drawerProps.onClose}
          >
            {cta.label}
          </Text>
        )}

        <Stack gap={0}>
          {links.map((link, index) => (
            <NavLink
              key={index}
              label={link.label}
              leftSection={
                link.icon &&
                React.cloneElement(link.icon as React.ReactElement, {
                  color: "var(--mantine-color-fontLightSecondary-5)",
                })
              }
              rightSection={link.rightSection}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              component={link.href ? (Link as any) : "button"}
              to={link.href}
              onClick={link.onClick}
              styles={{
                label: {
                  fontSize: "var(--mantine-font-size-sm)",
                  color: "var(--mantine-color-fontLight-5)",
                },
                root: {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "var(--mantine-radius-sm)",
                  minHeight: 48,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                },
              }}
              py="sm"
              px="md"
              mb="xs"
            />
          ))}
        </Stack>

        {children}
      </Stack>
    </Drawer>
  );
}
